import { Button } from "@material-ui/core";
import { Link } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import Tooltip from "@material-ui/core/Tooltip";
// import { DisplayKeyValuePair } from "../commons/DisplayKeyValuePair";
import { STATUS_MAP } from "src/helpers/constants";
import { Card, CardHeader } from "../commons/Card";
import { Chip } from "../commons/Chip";
import { SchedulesList } from "../commons/SchedulesList";
import { DashboardEmptyState } from "../icons/all/dashboard/DashboardEmptyState";
import { Twitter } from "../icons/all/twitter";
import { Instagram } from "../icons/all/instagram";
import { CARD, GRID, LAYOUT } from "../../theme/themeConstants";
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import EventNoteIcon from '@material-ui/icons/EventNote';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import ServiceBox from './ServiceBox';
import Table from '../commons/Table/Table';
import StatusChip from 'src/components/commons/StatusChip';
import { useSelector } from 'react-redux';
import { getVendorMatches } from 'src/api/vendorServices';
import { ROLES } from 'src/helpers/constants';

export function VendorDashboard({ activeVendorServicesWithMatches = [], allVendorServices = [] }) {
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [matches, setMatches] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const { authUser } = useSelector(({ auth }) => auth);

	useEffect(() => {
		async function fetchMatches() {
			try {
				setLoading(true);
				const data = await getVendorMatches(authUser.id);
				console.log("API Response from founder-vendor-matches:", data);
				setMatches(data || []);
			} catch (e) {
				console.error("Error fetching vendor matches:", e);
				setError(true);
			} finally {
				setLoading(false);
			}
		}
		if (authUser?.id) fetchMatches();
	}, [authUser?.id]);

	const dummySchedules = [
		{
			id: 1,
			title: "Business Strategy Session",
			date: "2024-03-20",
			time: "10:00 AM",
			status: "Upcoming"
		},
	];

	// Custom status renderer with background colors
	const renderStatus = (value) => {
		return <StatusChip status={value} />;
	};

	// Match Found columns
	const matchColumns = [
		{
			header: 'Offered Service',
			accessor: 'offered_service',
			align: 'left',
		},
		{ 
			header: 'Match Status', 
			accessor: 'status', 
			align: 'center', 
			render: (_, row) => <StatusChip status={row.status} />
		},
		{ 
			header: 'Call Status', 
			accessor: 'call_status', 
			align: 'center', 
			render: (_, row) => <StatusChip status={row.call_status} />
		},
		{
			header: 'Founder',
			accessor: 'founder_name',
			align: 'center',
			render: (value, row) => {
				const status = row.status?.toLowerCase() === 'active';
				return status ? value : '****';
			},
		},
		{
			header: 'Founder Email',
			accessor: 'founder_email',
			align: 'center',
			render: (value, row) => {
				const status = row.status?.toLowerCase() === 'active';
				return status ? value : '****';
			},
		},
		{
			header: 'Action',
			accessor: 'action',
			align: 'right',
			render: (_, row) => (
				<Tooltip title="Coming Soon" arrow placement="top">
					<span>
						<Button
							variant="contained"
							disabled={true}
							style={{
								backgroundColor: '#bdbdbd',
								color: '#fff',
								cursor: 'not-allowed',
							}}
						>
							Schedule Meeting
						</Button>
					</span>
				</Tooltip>
			),
		},
	];

	const handlePageChange = (event, newPage) => {
		setPage(newPage);
	};

	const handleRowsPerPageChange = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	// Founder bookings columns
	const founderColumns = [
		{
			header: 'Name',
			accessor: 'founderName',
			align: 'left',
		},
		{
			header: 'Amount Committed',
			accessor: 'amount',
			align: 'center',
			render: (value) => `$${value.toLocaleString()}`,
		},
		{
			header: 'Date of Agreement',
			accessor: 'agreementDate',
			align: 'center',
			render: (value) => new Date(value).toLocaleDateString(),
		},
		{
			header: 'Status',
			accessor: 'status',
			align: 'center',
			render: renderStatus,
		},
		{
			header: 'Action',
			accessor: 'action',
			align: 'right',
			render: (_, row) => (
				<Button
					variant="contained"
					color="primary"
					component={Link}
					to={`/booking/${row.id}/details`}
				>
					View Details
				</Button>
			),
		},
	];

	// Example founder bookings data
	const founderData = [
		{
			id: 1,
			founderName: 'John Smith',
			amount: 50000,
			agreementDate: '2024-03-15',
			status: 'ACTIVE',
		},
		{
			id: 2,
			founderName: 'Sarah Johnson',
			amount: 75000,
			agreementDate: '2024-03-18',
			status: 'IN PROGRESS',
		},
		{
			id: 3,
			founderName: 'Michael Brown',
			amount: 100000,
			agreementDate: '2024-03-20',
			status: 'COMPLETED',
		},
	];

	return (
		<div className="grid grid-cols-1 gap-4">
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[250px]">
				<Card className={`${CARD.DEFAULT} ${GRID.COL_SPAN.HALF}`}>
					<CardHeader>
						<span className="text-lg">Our Services Offered</span>
						<span className="text-primary cursor-pointer"><a href="/services">View All</a></span>
					</CardHeader>
					<div className="grid grid-cols-3 gap-4 p-4">
						{allVendorServices.data.slice(0, 3).map((service) => (
							<ServiceBox 
								key={service.id}
								id={service.id}
								title={service.name}
								description={service.description}
								// description={`Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.`}
								//cost={`${service.cost}$300 - $500`}
								// cost={`$300 - $500`}
							/>
						))}
					</div>
				</Card>
				<Card className={`${CARD.DEFAULT} opacity-50`}>
					<CardHeader>
						<span className="text-lg">Schedules</span>
						<Tooltip title="Coming Soon" arrow placement="top">
							<span className="text-primary cursor-not-allowed">
								<span>View All</span>
							</span>
						</Tooltip>
					</CardHeader>
					<div className="grid grid-cols-1 gap-4 p-4">
						<ServiceBox
							title="Coming Soon"
							viewText="Details"
							showCost={false}
							customContent={
								<div className="flex flex-col gap-2 text-slate-600">
									<div className="flex items-center gap-2">
										<EventNoteIcon fontSize="small" />
										<span>Schedule management feature will be available soon</span>
									</div>
								</div>
							}
						/>
					</div>
				</Card>
			</div>
			<Card className="!min-h-[250px]">
				<div className="p-4 rounded-t-md border border-b-gray-200 flex justify-between items-center">
					<h2 className="text-lg">Match Found</h2>
					<span className="text-primary cursor-pointer">
						<a href="/matches">See all</a>
					</span>
				</div>
				<div className="max-h-[400px] overflow-y-auto">
					{loading ? (
						<div className="p-8 text-center text-slate-400">Loading...</div>
					) : error ? (
						<div className="p-8 text-center text-red-400">Error loading matches</div>
					) : matches.length ? (
						<Table
							columns={matchColumns}
							data={matches}
							loading={false}
							error={false}
							page={page}
							rowsPerPage={rowsPerPage}
							totalCount={matches.length}
							onPageChange={handlePageChange}
							onRowsPerPageChange={handleRowsPerPageChange}
							stickyHeader
							className="border-0"
							emptyStateMessage="There are no pending matches at the moment"
						/>
					) : (
						<DashboardEmptyState 
							title="No Matches Found"
							description="You have no matches at the moment. Once a match is found, it will appear here."
						/>
					)}
				</div>
			</Card>
			{/* <SuccessfulMatches acceptedMatches={acceptedMatches} currentRole={currentRole} /> */}
			{/* <PendingMatches pendingMatches={acceptedMatches} currentRole={currentRole} /> */}
			<hr className="mb-4" />
			<Card className="!min-h-[250px]">
				<div className="p-4 rounded-t-md border border-b-gray-200 flex justify-between items-center">
					<h2 className="text-lg">Bookings</h2>
					<Tooltip title="Coming Soon" arrow placement="top">
						<span className="text-primary cursor-not-allowed">
							<span>See all</span>
						</span>
					</Tooltip>
				</div>
				<div className="max-h-[400px] overflow-y-auto opacity-50">
					<Table
						columns={founderColumns}
						data={[]}
						loading={false}
						error={false}
						emptyStateMessage="Booking management feature will be available soon"
						pagination={{
							page: 0,
							rowsPerPage: 5,
							totalCount: 0,
							onPageChange: () => {},
							onRowsPerPageChange: () => {},
						}}
					/>
				</div>
			</Card>
		</div>
	);
}
